import { useState, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Users,
  UserPlus,
  UserCog,
  Users2,
  CalendarDays,
  CalendarCheck,
  Receipt,
  CreditCard,
  AlertCircle,
  Banknote,
  Wallet,
  Calculator,
  FileText,
  BarChart3,
  Settings,
  Menu,
  X,
  Bell,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  LogOut,
  User,
  UtensilsCrossed,
  ShoppingBag,
  ShoppingCart,
  Package,
  FileBadge,
  UserX,
  Search,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpCircle,
  BookOpen,
  Sparkles,
  Bot,
  MessageCircle,
  ShieldAlert,
} from 'lucide-react';
import { useUIStore } from '../stores/uiStore';
import { useAuthStore } from '../stores/authStore';
import { useNotificationStore } from '../stores/notificationStore';
import api from '../lib/api';
import LanguageToggle from '../components/LanguageToggle';
import NotificationBell from '../components/NotificationBell';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import AiCreditsBadge from '../components/AiCreditsBadge';
import ImpersonationBanner from '../components/ImpersonationBanner';
import { cn } from '../lib/utils';

/* ─────────────────────────── Navigation Structure ─────────────────────────── */

const navGroups = [
  {
    id: 'dashboard',
    roles: ['admin', 'accountant', 'principal'],
    items: [
      { path: '/dashboard', icon: LayoutDashboard, labelKey: 'nav.dashboard' },
    ],
  },
  {
    id: 'students',
    groupKey: 'nav.students',
    groupIcon: Users,
    basePath: '/students',
    roles: ['admin', 'principal', 'teacher'],
    items: [
      { path: '/students', icon: Users, labelKey: 'nav.studentsList', end: true, roles: ['admin', 'principal'] },
      { path: '/students/new', icon: UserPlus, labelKey: 'nav.addStudent', roles: ['admin'] },
      { path: '/students/attendance', icon: CalendarCheck, labelKey: 'nav.attendance' },
      { path: '/students/promotion', icon: ArrowUpCircle, labelKey: 'nav.studentPromotion', roles: ['admin', 'principal'] },
      { path: '/students/grades', icon: BookOpen, labelKey: 'nav.courseGrades' },
    ],
  },
  {
    id: 'ai',
    groupKey: 'nav.aiFeatures',
    groupIcon: Sparkles,
    basePath: '/academics',
    roles: ['admin', 'principal', 'teacher'],
    items: [
      { path: '/academics/report-comments', icon: Bot, labelKey: 'nav.reportComments' },
      { path: '/academics/student-risk', icon: ShieldAlert, labelKey: 'ai.studentRisk', roles: ['admin', 'principal'] },
      { path: '/academics/parent-messages', icon: MessageCircle, labelKey: 'ai.parentMessage', roles: ['admin', 'principal'] },
      { path: '/academics/fee-reminders', icon: Banknote, labelKey: 'ai.feeReminders', roles: ['admin', 'accountant'] },
    ],
  },
  {
    id: 'fees',
    groupKey: 'nav.fees',
    groupIcon: Receipt,
    basePath: '/fees',
    roles: ['admin', 'accountant', 'principal'],
    items: [
      { path: '/fees', icon: Receipt, labelKey: 'nav.feeSchedules', end: true },
      { path: '/fees/payment', icon: CreditCard, labelKey: 'nav.recordPayment', roles: ['admin', 'accountant'] },
      { path: '/fees/overdue', icon: AlertCircle, labelKey: 'nav.overdueList', badgeKey: 'overdue' },
      { path: '/invoices', icon: FileBadge, labelKey: 'nav.invoices', roles: ['admin', 'accountant'] },
    ],
  },
  {
    id: 'hr',
    groupKey: 'nav.hr',
    groupIcon: UserCog,
    basePath: '/hr',
    roles: ['admin', 'hr'],
    items: [
      { path: '/hr/employees', icon: Users2, labelKey: 'nav.hrEmployees' },
      { path: '/hr/leave', icon: CalendarDays, labelKey: 'nav.leaveRequests', end: true },
      { path: '/hr/leave/balances', icon: CalendarCheck, labelKey: 'nav.leaveBalances' },
      { path: '/hr/absences', icon: UserX, labelKey: 'nav.absences' },
      { path: '/hr/advances', icon: Wallet, labelKey: 'nav.advances' },
      { path: '/hr/salaries', icon: Banknote, labelKey: 'nav.employeeSalaries' },
    ],
  },
  {
    id: 'payroll',
    groupKey: 'nav.payrollSection',
    groupIcon: Calculator,
    basePath: '/payroll',
    roles: ['admin', 'accountant'],
    items: [
      { path: '/payroll', icon: FileText, labelKey: 'nav.payrollPeriods' },
    ],
  },
  {
    id: 'cafeteria',
    groupKey: 'nav.cafeteria',
    groupIcon: UtensilsCrossed,
    basePath: '/cafeteria',
    roles: ['admin', 'accountant', 'principal'],
    items: [
      { path: '/cafeteria/items', icon: ShoppingBag, labelKey: 'nav.cafeteriaItems' },
      { path: '/cafeteria/sales', icon: ShoppingCart, labelKey: 'nav.cafeteriaSales' },
      { path: '/cafeteria/purchases', icon: Package, labelKey: 'nav.cafeteriaPurchases' },
      { path: '/cafeteria/summary', icon: Calculator, labelKey: 'nav.cafeteriaSummary' },
    ],
  },
  {
    id: 'system',
    groupKey: 'nav.system',
    groupIcon: Settings,
    roles: ['admin', 'accountant', 'principal'],
    items: [
      { path: '/expenses', icon: Banknote, labelKey: 'nav.expenses', roles: ['admin', 'accountant'] },
      { path: '/budgets', icon: Wallet, labelKey: 'nav.budgets', roles: ['admin', 'accountant'] },
      { path: '/reports', icon: BarChart3, labelKey: 'nav.reports', roles: ['admin', 'accountant', 'principal'] },
      { path: '/notifications', icon: Bell, labelKey: 'nav.notifications', badgeKey: 'notifications' },
      { path: '/settings', icon: Settings, labelKey: 'nav.settings', roles: ['admin'] },
    ],
  },
];

/* ─────────────────────────── Sidebar ─────────────────────────── */

function Sidebar({ isOpen, onClose, collapsed, onToggleCollapse }) {
  const { t } = useTranslation();
  const location = useLocation();
  const direction = useUIStore((s) => s.direction);
  const schoolProfile = useUIStore((s) => s.schoolProfile);
  const setSchoolProfile = useUIStore((s) => s.setSchoolProfile);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const unreadCount = useNotificationStore((s) => s.unreadCount);

  // Determine which group is active based on current path
  const activeGroupId = navGroups.find((g) => {
    if (g.basePath && location.pathname.startsWith(g.basePath)) return true;
    return g.items.some((item) => location.pathname === item.path || location.pathname.startsWith(item.path + '/'));
  })?.id || 'dashboard';

  const [expandedGroups, setExpandedGroups] = useState(new Set([activeGroupId]));

  // Auto-expand the group of the current route when navigating
  useEffect(() => {
    if (activeGroupId) {
      setExpandedGroups((prev) => {
        const next = new Set(prev);
        next.add(activeGroupId);
        return next;
      });
    }
  }, [activeGroupId]);

  // School profile fetched by DashboardLayout root — no duplicate fetch here

  const toggleGroup = useCallback((groupId) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);

  const schoolName = direction === 'rtl'
    ? (schoolProfile?.name_ar || t('appName'))
    : (schoolProfile?.name_en || schoolProfile?.name_ar || t('appName'));

  const Chevron = direction === 'rtl' ? ChevronLeft : ChevronRight;

  const getBadgeCount = (key) => {
    if (key === 'notifications') return unreadCount || 0;
    return 0;
  };

  const userName = user?.name || user?.username || t('roles.admin');
  const userInitial = userName.charAt(0).toUpperCase();
  const userRole = user?.role ? t(`roles.${user.role}`, user.role) : '';

  const handleLogout = () => {
    if (window.confirm(t('auth.logoutConfirm'))) {
      logout();
    }
  };

  // Filter nav groups and items based on RBAC
  const filteredNavGroups = navGroups
    .filter((group) => !group.roles || group.roles.includes(user?.role))
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !item.roles || item.roles.includes(user?.role)),
    }))
    .filter((group) => group.items.length > 0);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed top-0 z-50 h-full flex flex-col bg-white border-gray-200 transition-all duration-300 lg:static lg:z-auto',
          direction === 'rtl' ? 'right-0 border-l' : 'left-0 border-r',
          collapsed ? 'w-[68px]' : 'w-64',
          isOpen
            ? 'translate-x-0 shadow-xl'
            : direction === 'rtl'
              ? 'translate-x-full lg:translate-x-0'
              : '-translate-x-full lg:translate-x-0'
        )}
      >
        {/* ── Logo area ── */}
        <div className={cn('flex items-center h-16 border-b border-gray-100 shrink-0', collapsed ? 'justify-center px-2' : 'justify-between px-4')}>
          {collapsed ? (
            <div className="w-10 h-10 rounded-xl bg-primary-600 flex items-center justify-center overflow-hidden">
              {schoolProfile?.logo_url ? (
                <img src={schoolProfile.logo_url} alt="logo" className="w-full h-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              ) : (
                <span className="text-white font-bold text-base">م</span>
              )}
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-primary-600 flex items-center justify-center overflow-hidden shrink-0">
                  {schoolProfile?.logo_url ? (
                    <img src={schoolProfile.logo_url} alt="logo" className="w-full h-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  ) : (
                    <span className="text-white font-bold text-base">م</span>
                  )}
                </div>
                <div className="min-w-0">
                  <h1 className="text-sm font-bold text-gray-900 truncate">{schoolName}</h1>
                  <p className="text-[10px] text-gray-400 font-medium">Qalamy</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"
              >
                <X size={18} />
              </button>
            </>
          )}
        </div>

        {/* ── Navigation ── */}
        <nav className={cn('flex-1 overflow-y-auto py-3', collapsed ? 'px-2' : 'px-3')}>
          <div className="space-y-1">
            {filteredNavGroups.map((group) => {
              const isGroupExpanded = expandedGroups.has(group.id);
              const isActiveGroup = activeGroupId === group.id;
              const isSingleItem = !group.groupKey;
              const GroupIcon = group.groupIcon;

              // Single item groups (Dashboard)
              if (isSingleItem) {
                return group.items.map((item) => {
                  const Icon = item.icon;
                  const badge = item.badgeKey ? getBadgeCount(item.badgeKey) : 0;
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end={!!item.end}
                      onClick={onClose}
                      className={({ isActive }) => cn(
                        'flex items-center gap-3 rounded-lg text-sm font-medium transition-all duration-150 group relative',
                        collapsed ? 'justify-center p-2.5' : 'px-3 py-2.5',
                        isActive
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      )}
                      title={collapsed ? t(item.labelKey) : undefined}
                    >
                      {({ isActive }) => (
                        <>
                          {isActive && (
                            <div className={cn(
                              'absolute top-1 bottom-1 w-[3px] rounded-full bg-primary-600',
                              direction === 'rtl' ? 'right-0' : 'left-0'
                            )} />
                          )}
                          <Icon size={20} strokeWidth={isActive ? 2.2 : 1.7} className="shrink-0" />
                          {!collapsed && <span className="truncate">{t(item.labelKey)}</span>}
                          {badge > 0 && !collapsed && (
                            <span className="ms-auto min-w-[20px] h-5 bg-red-500 rounded-full text-white text-[10px] font-bold flex items-center justify-center px-1">
                              {badge > 99 ? '99+' : badge}
                            </span>
                          )}
                          {badge > 0 && collapsed && (
                            <span className="absolute -top-0.5 -end-0.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white" />
                          )}
                        </>
                      )}
                    </NavLink>
                  );
                });
              }

              // Collapsible group
              return (
                <div key={group.id} className="mt-1">
                  {/* Group header */}
                  <button
                    type="button"
                    onClick={() => collapsed ? null : toggleGroup(group.id)}
                    className={cn(
                      'w-full flex items-center gap-3 rounded-lg text-sm font-medium transition-all duration-150',
                      collapsed ? 'justify-center p-2.5' : 'px-3 py-2',
                      isActiveGroup
                        ? 'text-primary-700'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    )}
                    title={collapsed ? t(group.groupKey) : undefined}
                  >
                    {GroupIcon && <GroupIcon size={collapsed ? 20 : 18} strokeWidth={isActiveGroup ? 2 : 1.7} className="shrink-0" />}
                    {!collapsed && (
                      <>
                        <span className="truncate flex-1 text-start">{t(group.groupKey)}</span>
                        <ChevronDown
                          size={14}
                          className={cn(
                            'text-gray-400 transition-transform duration-200 shrink-0',
                            isGroupExpanded && 'rotate-180'
                          )}
                        />
                      </>
                    )}
                  </button>

                  {/* Group items */}
                  {!collapsed && (
                    <div
                      className={cn(
                        'overflow-hidden transition-all duration-200',
                        isGroupExpanded ? 'max-h-96 opacity-100 mt-0.5' : 'max-h-0 opacity-0'
                      )}
                    >
                      <div className={cn('space-y-0.5', direction === 'rtl' ? 'pr-4' : 'pl-4')}>
                        {group.items.map((item) => {
                          const Icon = item.icon;
                          const badge = item.badgeKey ? getBadgeCount(item.badgeKey) : 0;
                          return (
                            <NavLink
                              key={item.path}
                              to={item.path}
                              end={!!item.end}
                              onClick={onClose}
                              className={({ isActive }) => cn(
                                'flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150 relative',
                                isActive
                                  ? 'bg-primary-50 text-primary-700'
                                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                              )}
                            >
                              {({ isActive }) => (
                                <>
                                  {isActive && (
                                    <div className={cn(
                                      'absolute top-1.5 bottom-1.5 w-[3px] rounded-full bg-primary-600',
                                      direction === 'rtl' ? 'right-0' : 'left-0'
                                    )} />
                                  )}
                                  <Icon size={17} strokeWidth={isActive ? 2.2 : 1.7} className="shrink-0" />
                                  <span className="truncate">{t(item.labelKey)}</span>
                                  {badge > 0 && (
                                    <span className="ms-auto min-w-[20px] h-5 bg-red-500 rounded-full text-white text-[10px] font-bold flex items-center justify-center px-1">
                                      {badge > 99 ? '99+' : badge}
                                    </span>
                                  )}
                                </>
                              )}
                            </NavLink>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Collapsed: show tooltip-style popout on hover via group items as direct nav */}
                  {collapsed && (
                    <div className="space-y-0.5 mt-0.5">
                      {group.items.map((item) => {
                        const Icon = item.icon;
                        const badge = item.badgeKey ? getBadgeCount(item.badgeKey) : 0;
                        return (
                          <NavLink
                            key={item.path}
                            to={item.path}
                            end={!!item.end}
                            onClick={onClose}
                            className={({ isActive }) => cn(
                              'flex items-center justify-center p-2 rounded-lg transition-all duration-150 relative',
                              isActive
                                ? 'bg-primary-50 text-primary-700'
                                : 'text-gray-400 hover:bg-gray-50 hover:text-gray-600'
                            )}
                            title={t(item.labelKey)}
                          >
                            {({ isActive }) => (
                              <>
                                {isActive && (
                                  <div className={cn(
                                    'absolute top-1 bottom-1 w-[3px] rounded-full bg-primary-600',
                                    direction === 'rtl' ? 'right-0' : 'left-0'
                                  )} />
                                )}
                                <Icon size={18} strokeWidth={isActive ? 2.2 : 1.7} />
                                {badge > 0 && (
                                  <span className="absolute -top-0.5 -end-0.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white" />
                                )}
                              </>
                            )}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

        {/* ── Collapse toggle (desktop only) ── */}
        <div className="hidden lg:flex justify-center py-2 border-t border-gray-100">
          <button
            onClick={onToggleCollapse}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
            title={collapsed ? t('nav.expandSidebar', 'Expand') : t('nav.collapseSidebar', 'Collapse')}
          >
            {collapsed
              ? (direction === 'rtl' ? <ChevronsLeft size={18} /> : <ChevronsRight size={18} />)
              : (direction === 'rtl' ? <ChevronsRight size={18} /> : <ChevronsLeft size={18} />)
            }
          </button>
        </div>

        {/* ── AI Credits badge ── */}
        <AiCreditsBadge collapsed={collapsed} />

        {/* ── User footer ── */}
        <div className={cn('border-t border-gray-100 shrink-0', collapsed ? 'p-2' : 'p-3')}>
          {collapsed ? (
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors"
              title={t('userMenu.logout')}
            >
              <LogOut size={18} />
            </button>
          ) : (
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                <span className="text-sm font-bold text-primary-700">{userInitial}</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-800 truncate">{userName}</p>
                {userRole && <p className="text-[10px] text-gray-400 font-medium">{userRole}</p>}
              </div>
              <button
                onClick={handleLogout}
                className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors shrink-0"
                title={t('userMenu.logout')}
              >
                <LogOut size={16} />
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

/* ─────────────────────────── Top Header ─────────────────────────── */

function TopHeader({ onMenuClick }) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const direction = useUIStore((s) => s.direction);

  return (
    <header className="sticky top-0 z-30 h-14 bg-white/80 backdrop-blur-md border-b border-gray-200/80">
      <div className="flex items-center justify-between h-full px-4">
        {/* Left: mobile menu */}
        <div className="flex items-center gap-3">
          <button
            onClick={onMenuClick}
            className="p-2 rounded-lg hover:bg-gray-100 lg:hidden transition-colors"
          >
            <Menu size={20} className="text-gray-600" />
          </button>

          {/* Search placeholder */}
          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100/80 text-gray-400 text-sm cursor-pointer hover:bg-gray-100 transition-colors min-w-[220px]">
            <Search size={16} />
            <span>{t('nav.search', 'Search...')}</span>
            <kbd className={cn('text-[10px] bg-white rounded px-1.5 py-0.5 border border-gray-200 font-mono', direction === 'rtl' ? 'me-auto' : 'ms-auto')}>⌘K</kbd>
          </div>
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-1.5">
          <LanguageToggle />
          <NotificationBell />

          {/* User avatar — visible on mobile where sidebar footer is hidden */}
          <div className="lg:hidden flex items-center">
            <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center ms-1">
              <span className="text-xs font-bold text-primary-700">
                {(user?.name || user?.username || 'A').charAt(0).toUpperCase()}
              </span>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

/* ─────────────────────────── Layout Root ─────────────────────────── */

export default function DashboardLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const schoolProfile = useUIStore((s) => s.schoolProfile);
  const setSchoolProfile = useUIStore((s) => s.setSchoolProfile);

  // Fetch school profile once on mount
  useEffect(() => {
    if (schoolProfile) return;
    api.get('/settings/school-profile')
      .then((res) => {
        const data = res?.data;
        if (data && (data.name_ar || data.name_en)) {
          setSchoolProfile(data);
        }
      })
      .catch(() => {});
  }, [schoolProfile, setSchoolProfile]);

  const isImpersonating = useAuthStore((s) => s.isImpersonating);

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-gray-50">
      <ImpersonationBanner />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar
          isOpen={mobileOpen}
          onClose={() => setMobileOpen(false)}
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed(!collapsed)}
        />

        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          <TopHeader onMenuClick={() => setMobileOpen(true)} />

          <main className="flex-1 overflow-y-auto p-4 sm:p-6">
            <Breadcrumbs />
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
